import { Button, Checkbox, Spinner } from "@heroui/react";
import { PDFDownloadLink, pdf } from "@react-pdf/renderer";
import { useEffect, useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Api } from "../api";
import { useResumeBuilder } from "../context";
import { useNavigateWithParams } from "../hooks";
import { ResumePDF } from "../resume-pdf";
import { ResumePDFViewer } from "./resume-pdf-viewer";

const ResumePreviewPage = () => {
    const navigate = useNavigateWithParams();
    const { resumeContent, privacy, design, token } =
        useResumeBuilder();
    const [policyAccepted, setPolicyAccepted] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [resumeBlob, setResumeBlob] = useState<Blob | null>(null);

    const resumeUrl = useMemo(() => {
        if (!resumeBlob) {
            return null;
        }
        return URL.createObjectURL(resumeBlob);
    }, [resumeBlob]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    useEffect(() => {
        pdf(<ResumePDF resumeContent={resumeContent} />)
            .toBlob()
            .then(async (blob) => setResumeBlob(blob));
    }, [JSON.stringify(resumeContent)]);

    const mutation = useMutation({
        mutationFn: async () => {
            await Promise.all([
                Api.sendResume({
                    token,
                    resumeBlob,
                    email: resumeContent.step1.email,
                }),
                Api.updatePersonalInfo({
                    token,
                    data: {
                        name: resumeContent.step1.name,
                        emailAddress: resumeContent.step1.email,
                        phoneNumber: resumeContent.step1.phone,
                    },
                }),
            ]);
        },

        onSuccess: () => {
            setSubmitError(null);
            navigate("/sent");
        },

        onError: () => {
            setSubmitError(
                "Beim Absenden des Lebenslaufs ist ein Fehler aufgetreten. Bitte versuche es später erneut.",
            );
        }
    });

    const handleSubmit = async () => {
        if (!token) {
            return;
        }

        await mutation.mutate();
    };

    return (
        <div className="min-h-full bg-gray-50 p-8">
            <div className="mx-auto flex max-w-2xl flex-col gap-6">
                <h2 className="text-center font-bold text-[30px] text-gray-900">
                    Lebenslauf Vorschau
                </h2>

                {resumeBlob ? (
                    <ResumePDFViewer file={resumeUrl} withNav={true} />
                ) : (
                    <Spinner className="mt-2" />
                )}

                <div className="mt-8 flex flex-col items-center">
                    {token && (
                        <div className="mb-4 flex items-center gap-2">
                            <Checkbox
                                isSelected={policyAccepted}
                                onValueChange={() => {
                                    setPolicyAccepted(!policyAccepted);
                                }}
                            />
                            <button
                                type="button"
                                className="w-min xs:text-nowrap text-left"
                                onClick={() => {
                                    setPolicyAccepted(!policyAccepted);
                                }}
                            >
                                *Ich akzeptiere die{" "}
                                {privacy ? (
                                    <a
                                        className="underline"
                                        target="_blank"
                                        rel="noreferrer"
                                        href={privacy}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        Datenschutzbestimmungen
                                    </a>
                                ) : (
                                    "Datenschutzbestimmungen"
                                )}
                            </button>
                        </div>
                    )}
                    <div className="flex w-full flex-col items-center justify-center gap-2">
                        {token ? (
                            <div className="flex w-full max-w-md flex-col items-center gap-1">
                                <Button
                                    size="lg"
                                    isLoading={mutation.isPending}
                                    isDisabled={
                                        !policyAccepted || mutation.isPending
                                    }
                                    onPress={handleSubmit}
                                    style={
                                        !policyAccepted
                                            ? {}
                                            : {
                                                  backgroundColor:
                                                      design.resumePrimaryColor,
                                                  color: design.resumePrimaryContrastColor,
                                              }
                                    }
                                >
                                    Lebenslauf abschicken
                                </Button>
                                {submitError && (
                                    <div className="w-full text-center text-danger text-tiny">
                                        {submitError}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <PDFDownloadLink
                                document={
                                    <ResumePDF resumeContent={resumeContent} />
                                }
                                fileName="Lebenslauf.pdf"
                            >
                                <Button
                                    as="div"
                                    size="lg"
                                    style={{
                                        backgroundColor:
                                            design.resumePrimaryColor,
                                        color: design.resumePrimaryContrastColor,
                                    }}
                                >
                                    Lebenslauf herunterladen
                                </Button>
                            </PDFDownloadLink>
                        )}
                        <Button
                            onPress={() => navigate(-1)}
                            variant="light"
                            type="button"
                            isDisabled={mutation.isPending}
                        >
                            ← Zurück
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export { ResumePreviewPage };
